import { useEffect, useRef, useState } from "react";
import { Upload, X } from "lucide-react";
import { Button, Input } from "@/components/ui";
import { trpc } from "@/lib/trpc";
import { useAppStore } from "@/stores/app.store";

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result).split(",")[1] ?? "");
    r.onerror = reject;
    r.readAsDataURL(file);
  });
}

export function BrandingTab() {
  const utils = trpc.useUtils();
  const addToast = useAppStore((s) => s.addToast);
  const branding = trpc.branding.get.useQuery();
  const fileRef = useRef<HTMLInputElement>(null);
  const watermarkFileRef = useRef<HTMLInputElement>(null);

  const [accent, setAccent] = useState("#2B87C7");
  const [accentHover, setAccentHover] = useState("#216FA6");

  useEffect(() => {
    if (!branding.data) return;
    setAccent(branding.data.accentColor ?? "#2B87C7");
    setAccentHover(branding.data.accentHoverColor ?? "#216FA6");
  }, [branding.data]);

  const save = trpc.branding.update.useMutation({
    onSuccess: () => {
      utils.branding.get.invalidate();
      addToast({ type: "success", title: "Branding gespeichert" });
    },
    onError: (e) =>
      addToast({ type: "error", title: "Fehler", description: e.message }),
  });
  const upload = trpc.asset.upload.useMutation({
    onSuccess: async (res) => {
      await save.mutateAsync({ logoAssetId: res.id });
      utils.branding.get.invalidate();
      addToast({ type: "success", title: "Logo hochgeladen" });
    },
    onError: (e) =>
      addToast({
        type: "error",
        title: "Upload fehlgeschlagen",
        description: e.message,
      }),
  });
  const uploadWatermark = trpc.asset.upload.useMutation({
    onSuccess: async (res) => {
      await save.mutateAsync({ watermarkAssetId: res.id });
      utils.branding.get.invalidate();
      addToast({ type: "success", title: "Wasserzeichen hochgeladen" });
    },
    onError: (e) =>
      addToast({
        type: "error",
        title: "Upload fehlgeschlagen",
        description: e.message,
      }),
  });
  const removeWatermark = trpc.branding.update.useMutation({
    onSuccess: () => {
      utils.branding.get.invalidate();
      addToast({ type: "success", title: "Wasserzeichen entfernt" });
    },
    onError: (e) =>
      addToast({ type: "error", title: "Fehler", description: e.message }),
  });

  const onFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const dataBase64 = await fileToBase64(file);
    upload.mutate({
      filename: file.name,
      mime: file.type || "image/png",
      dataBase64,
      kind: "logo",
    });
    e.target.value = "";
  };

  const onWatermarkFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const dataBase64 = await fileToBase64(file);
    uploadWatermark.mutate({
      filename: file.name,
      mime: file.type || "image/png",
      dataBase64,
      kind: "watermark",
    });
    e.target.value = "";
  };

  const hexOk = (v: string) => /^#[0-9a-fA-F]{6}$/.test(v);

  return (
    <div className="p-6">
      <div className="max-w-3xl space-y-8">
        <section>
          <h3 className="text-sm font-semibold text-text-primary mb-3">Logo</h3>
          <div className="flex items-center gap-4">
            <div className="w-40 h-16 rounded-md bg-bg-active border border-border flex items-center justify-center overflow-hidden">
              {branding.data?.logoUrl ? (
                <img
                  src={branding.data.logoUrl}
                  alt="Logo"
                  className="max-h-14 max-w-[150px] object-contain"
                />
              ) : (
                <span className="text-2xs text-text-tertiary">kein Logo</span>
              )}
            </div>
            <input
              ref={fileRef}
              type="file"
              accept="image/png,image/jpeg,image/webp,image/svg+xml"
              className="hidden"
              onChange={onFile}
            />
            <Button
              variant="secondary"
              onClick={() => fileRef.current?.click()}
              loading={upload.isPending}
            >
              <Upload className="w-4 h-4" /> Logo wählen
            </Button>
          </div>
          <p className="text-xs text-text-tertiary mt-2">
            PNG, JPG, WebP oder SVG, max. 3 MB.
          </p>
        </section>

        <section>
          <h3 className="text-sm font-semibold text-text-primary mb-3">
            Wasserzeichen
          </h3>
          <div className="flex items-center gap-4">
            <div className="w-40 h-16 rounded-md bg-bg-active border border-border flex items-center justify-center overflow-hidden">
              {branding.data?.watermarkUrl ? (
                <img
                  src={branding.data.watermarkUrl}
                  alt="Wasserzeichen"
                  className="max-h-14 max-w-[150px] object-contain"
                />
              ) : (
                <span className="text-2xs text-text-tertiary">
                  kein Wasserzeichen
                </span>
              )}
            </div>
            <input
              ref={watermarkFileRef}
              type="file"
              accept="image/png,image/jpeg,image/webp,image/svg+xml"
              className="hidden"
              onChange={onWatermarkFile}
            />
            <Button
              variant="secondary"
              onClick={() => watermarkFileRef.current?.click()}
              loading={uploadWatermark.isPending}
            >
              <Upload className="w-4 h-4" /> Wasserzeichen wählen
            </Button>
            {branding.data?.watermarkAssetId && (
              <Button
                variant="ghost"
                onClick={() =>
                  removeWatermark.mutate({ watermarkAssetId: null })
                }
                loading={removeWatermark.isPending}
              >
                <X className="w-4 h-4" /> Entfernen
              </Button>
            )}
          </div>
          <p className="text-xs text-text-tertiary mt-2">
            PNG, JPG, WebP oder SVG, max. 3 MB. Erscheint zentriert und dezent
            im Hintergrund von Angebots- und Rechnungs-PDFs (hinter den
            Positionen) — nicht in der übrigen CRM-Oberfläche.
          </p>
        </section>

        <section>
          <h3 className="text-sm font-semibold text-text-primary mb-3">
            Farben
          </h3>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="label">Akzentfarbe</label>
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  value={hexOk(accent) ? accent : "#2B87C7"}
                  onChange={(e) => setAccent(e.target.value)}
                  className="w-9 h-9 rounded border border-border bg-transparent"
                />
                <Input
                  value={accent}
                  onChange={(e) => setAccent(e.target.value)}
                />
              </div>
            </div>
            <div>
              <label className="label">Akzent (Hover)</label>
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  value={hexOk(accentHover) ? accentHover : "#216FA6"}
                  onChange={(e) => setAccentHover(e.target.value)}
                  className="w-9 h-9 rounded border border-border bg-transparent"
                />
                <Input
                  value={accentHover}
                  onChange={(e) => setAccentHover(e.target.value)}
                />
              </div>
            </div>
          </div>
        </section>

        <Button
          variant="primary"
          loading={save.isPending}
          disabled={!hexOk(accent) || !hexOk(accentHover)}
          onClick={() =>
            save.mutate({
              accentColor: accent,
              accentHoverColor: accentHover,
              pdfPrimaryColor: accent,
            })
          }
        >
          Speichern
        </Button>
      </div>
    </div>
  );
}
